import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { capitalize, groupBy, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import TableCombinationGroup from "~/components/table/table-combination-group";
import { useTable } from "~/hooks/use-table";
import { useTableManager } from "~/managers/use-table-manager";

export const Route = createFileRoute(
  "/_main/settings/_layout/table-combinations",
)({
  component: TableCombinations,
});

function TableCombinations() {
  const { t } = useTranslation("common");
  const { tableCombinations } = useTable();
  const { openTableCombinationDialog } = useTableManager();
  const [selectedGroupSize, setSelectedGroupSize] = useState<
    number | undefined
  >(undefined);

  const tableCombinationsByGroupSize = Object.entries(
    groupBy(tableCombinations, "groupSize"),
  ).map(([groupSize, combinations]) => ({
    groupSize: parseInt(groupSize),
    combinations: combinations.length,
  }));

  const groupedColumns: ColumnDef<{
    groupSize: number;
    combinations: number;
  }>[] = [
    {
      id: "groupSize",
      accessorKey: "groupSize",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("TableCombinations.group_size")}
        />
      ),
      cell: ({ row: { original: tableCombination }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  tableCombination.groupSize.toString(),
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: "combinations",
      accessorKey: "combinations",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("TableCombinations.combinations")}
        />
      ),
      cell: ({ row: { original: tableCombination }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  tableCombination.combinations.toString(),
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("TableCombinations.page_title")}
        buttonAction={async () => {
          await openTableCombinationDialog();
        }}
        buttonLabel={capitalize(t("TableCombinations.new_combination"))}
      />
      <TableCombinationGroup
        groupSize={selectedGroupSize}
        onClose={() => setSelectedGroupSize(undefined)}
      />
      <DataTable
        defaultSorting={[{ id: t("TableCombinations.group_size"), desc: true }]}
        columns={groupedColumns}
        data={tableCombinationsByGroupSize}
        onSelect={(row) => {
          setSelectedGroupSize(row.groupSize);
        }}
      />
    </>
  );
}
